import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, X } from '../../ui/Icon';
import TimePicker from '../../ui/TimePicker';
import Spinner from '../../ui/Spinner';

interface Props {
  /** `null` means this day still uses hours typed by hand. */
  value: string | null;
  onChange: (startTime: string | null) => Promise<void>;
  /** Legs this day has no cached answer for; the schedule is short by their travel. */
  missingLegs: number;
}

/**
 * The hour a day starts — ADR-0010, and the piece the whole schedule hangs off.
 *
 * It is the DAY's hour, deliberately: keeping it on the first stop is what made
 * it disappear every time somebody reordered.
 *
 * It lives **on the day's own line**, right after the date, because that is
 * what it is — a fact about the day, not a setting. It used to be a labelled
 * box with its own clock button and a three-line explanation, which is a lot of
 * furniture for one number; and since ADR-0011 made it fill itself in, it is
 * furniture nobody asked for. The explanation moved into the tooltip: worth
 * reading once, not on every day forever.
 */
export function DayStartTime({ value, onChange, missingLegs }: Props) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  async function set(next: string | null) {
    setSaving(true);
    try {
      await onChange(next);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  const hint = missingLegs > 0 ? t('itinerary.schedule.noTravel') : t('itinerary.schedule.hint');

  // A day whose stops are all untimed has nothing to start from, so the hour
  // stays an offer rather than a guess (ADR-0010).
  if (!value) {
    return (
      <span className="inline-flex items-center gap-1.5 align-middle">
        <button
          type="button"
          disabled={saving}
          onClick={() => set('09:00')}
          title={hint}
          className="text-xs text-slate-400 underline decoration-dashed underline-offset-2 hover:text-teal-600 dark:hover:text-teal-400 transition-colors cursor-pointer disabled:opacity-50"
        >
          {t('itinerary.schedule.setDayStart')}
        </button>
        {saving && <Spinner size="xs" />}
      </span>
    );
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-2 align-middle">
        <TimePicker value={value} onChange={v => v && set(v)} className="w-[104px]" />
        <button
          type="button"
          onClick={() => set(null)}
          className="inline-flex items-center gap-1 text-2xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer"
        >
          <Icon as={X} size={11} />
          {t('itinerary.schedule.clear')}
        </button>
        {saving && <Spinner size="xs" />}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title={hint}
      className="text-xs text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors cursor-pointer"
    >
      {t('itinerary.schedule.from')}{' '}
      <span className="font-mono underline decoration-dashed underline-offset-2">{value}</span>
    </button>
  );
}
